import { AudioWave } from '@newo/ui';
import type { FC } from 'react';
import { cn } from '@/utils';
import CreatorChatFlowChat from './components/CreatorChatFlowChat';
import CreatorChatFlowIntroStatus from './components/CreatorChatFlowIntroStatus';
import CreatorChatFlowNextSteps from './components/CreatorChatFlowNextSteps';
import CreatorChatFlowReadyCta from './components/CreatorChatFlowReadyCta';
import { useCreatorChatFlowController } from './hooks/useCreatorChatFlowController.tsx';

const CreatorChatFlow: FC = () => {
	const {
		audioLevel,
		completedHearts,
		emilyFinalX,
		emilyHeight,
		emilyOverflowBottom,
		emilyOverflowTop,
		emilyWidth,
		emilyY,
		handleStartCall,
		hasStartedCallBefore,
		initialImageContainerRef,
		introOverlayOffsetY,
		isChatVisible,
		isCallStarted,
		isLoadingHistory,
		isReceptionistCreated,
		isReady,
		isSendingMessage,
		isStartingCall,
		messages,
		pendingCallCreationFinished,
		sendMessage,
		shouldShowNextSteps,
		stageHeight,
		transferredImageContainerRef
	} = useCreatorChatFlowController();

	return (
		<div className="min-h-full bg-white">
			<div
				className="relative overflow-visible"
				data-testid="creator-chat-flow-stage"
				style={{ minHeight: `${stageHeight}px` }}
			>
				<div className="absolute inset-0 z-20 flex items-center justify-center">
					<div className="relative flex items-center justify-center">
						{!isReceptionistCreated && (
							<div
								className="pointer-events-none relative"
								style={{ top: introOverlayOffsetY }}
							>
								<CreatorChatFlowIntroStatus completedHearts={completedHearts} />
							</div>
						)}

						<div
							className={cn('absolute transition-all ease-out', {
								'duration-[1200ms]': !isReceptionistCreated,
								'duration-[0ms]': isReceptionistCreated,
								'opacity-100 -translate-y-[132px] scale-100': isReady,
								'opacity-0 -translate-y-[108px] scale-[0.94]': !isReady,
								'pointer-events-none': isCallStarted
							})}
						>
							{isReady && !shouldShowNextSteps ? (
								<CreatorChatFlowReadyCta
									hasStartedCallBefore={hasStartedCallBefore}
									isStartingCall={isStartingCall}
									onStartCall={handleStartCall}
								/>
							) : null}
						</div>
					</div>
				</div>

				{isCallStarted ? (
					<div className="absolute inset-0 z-[15] flex items-center justify-center opacity-100 -translate-y-[88px] scale-100 transition-all duration-[700ms] ease-out">
						<AudioWave
							className="pointer-events-none relative h-[340px] w-[min(96vw,1520px)]"
							data-testid="creator-chat-flow-audio-wave"
							level={audioLevel}
						/>
					</div>
				) : null}

				{isReceptionistCreated && isChatVisible && !shouldShowNextSteps ? (
					<div className="pointer-events-none absolute inset-x-0 bottom-[36px] z-30 flex justify-center">
						<div className="pointer-events-auto">
							<CreatorChatFlowChat
								isLoading={isLoadingHistory}
								isSendingMessage={isSendingMessage}
								messages={messages}
								onSendMessage={sendMessage}
							/>
						</div>
					</div>
				) : null}

				{shouldShowNextSteps && pendingCallCreationFinished ? (
					<div className="pointer-events-none absolute inset-x-0 bottom-[36px] z-30 flex justify-center">
						<div className="pointer-events-auto">
							<CreatorChatFlowNextSteps
								agentName={pendingCallCreationFinished.agent_name}
								agentPhone={pendingCallCreationFinished.agent_phone}
								customerName={pendingCallCreationFinished.customer_name}
								ownerCallDatetime={
									pendingCallCreationFinished.owner_call_datetime
								}
							/>
						</div>
					</div>
				) : null}

				<div
					className="pointer-events-none absolute inset-x-0 z-10"
					data-testid="creator-chat-flow-emily"
					style={{
						bottom: -emilyOverflowBottom,
						top: -emilyOverflowTop
					}}
				>
					<div
						className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 scale-x-[0.95] scale-y-[1.04]"
						ref={initialImageContainerRef}
						style={{
							height: emilyHeight,
							left: isReceptionistCreated ? `${emilyFinalX}px` : '50%',
							top: emilyY,
							width: emilyWidth
						}}
					/>
					<div
						className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 scale-x-[0.95] scale-y-[1.04]"
						ref={transferredImageContainerRef}
						style={{
							height: emilyHeight,
							left: emilyFinalX,
							top: emilyY,
							width: emilyWidth
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default CreatorChatFlow;
